// frontend/client/src/App.js
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Context Providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './hooks/useToast';
import { SelectedMediaProvider } from './context/SelectedMediaContext';
import { ThemeProvider } from './context/ThemeContext';
import { useTheme as useAppliedTheme } from './hooks/useTheme';
import { DraftProvider } from './context/DraftContext';

// Components & Layouts
import LoadingSpinner from './components/LoadingSpinner';
import ChatbaseErrorBoundary from './components/ChatbaseErrorBoundary';
import DashboardLayout from './components/DashboardLayout';

// Public Pages
import LandingPageEnhanced from './components/LandingPage';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import BrandWizard from './components/BrandWizard';
import Waitlist from './components/Waitlist';
import PricingPage from './components/PricingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import ContactUs from './components/ContactUs';
import HelpCenter from './components/HelpCenter';
import About from './components/About'; // ✨ RE-ADDED ABOUT

// 🚀 DASHBOARD COMPONENTS
import DashboardOverview from './components/DashboardOverview';
import EditStudio from './components/EditStudio';
import FeedPreview from './components/FeedPreview';
import AdvancedAnalytics from './components/AdvancedAnalytics';
import MediaLibrary from './components/MediaLibrary';
import Campaigns from './components/Campaigns';
import AICoach from './components/AICoach';
import Calendar from './components/Calendar';
import Profile from './components/Profile';
import SavedContent from './components/SavedContent';
import AppSettings from './components/AppSettings';

// Initialize React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppWrapper() {
  const location = useLocation();

  // FIX: this was previously only called inside DashboardLayout, so any
  // route rendered outside it — most critically BrandWizard, the very
  // first real screen a new user sees — never got the CSS variables
  // (--text-main, --bg-surface, etc.) it depends on for every element,
  // rendering unreadable dark-on-dark text for every single new signup.
  // Calling it once here instead means it's active for any authenticated
  // page, regardless of which layout wraps it.
  useAppliedTheme();

  useEffect(() => {
    // FIX: this called window.posthog.capture('$pageview') — but PostHog
    // was never actually loaded anywhere (confirmed directly against
    // index.html, no script tag exists). This was silently doing nothing
    // since early development. GA4's gtag.js only auto-tracks the very
    // first page load, with no built-in awareness of client-side routing —
    // so every single React Router navigation past that first load was
    // genuinely invisible to Analytics until now.
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);

  return (
    <Suspense fallback={<LoadingSpinner size="large" />}>
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route path="/" element={<LandingPageEnhanced />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/pricing" element={<PricingPage />} />
        
        {/* ✨ RE-ADDED PUBLIC INFO ROUTES */}
        <Route path="/about" element={<About />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/contact-us" element={<ContactUs />} />
        
        <Route path="/help" element={<HelpCenter />} />

        {/* ONBOARDING */}
        <Route 
          path="/brand-setup" 
          element={
            <ProtectedRoute requireOnboarding={false}>
              <BrandWizard />
            </ProtectedRoute>
          } 
        />

        {/* 🚀 PROTECTED DASHBOARD ROUTES */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <ChatbaseErrorBoundary>
                <DashboardLayout />
              </ChatbaseErrorBoundary>
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardOverview />} />
          <Route path="edit-studio" element={<EditStudio />} />
          <Route path="preview" element={<FeedPreview />} />
          <Route path="analytics" element={<AdvancedAnalytics />} />
          <Route path="coach" element={<AICoach />} />
          <Route path="media" element={<MediaLibrary />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="saved" element={<SavedContent />} />
          <Route path="profile" element={<Profile />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="settings" element={<AppSettings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// ... (Rest of ProtectedRoute and App export remain unchanged) ...
function ProtectedRoute({ children, requireOnboarding = true }) {
  const { currentUser, userData, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading your workspace..." />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!userData || userData.fallback || userData.temporary) {
    return children;
  }

  const hasBrandBasics = !!(
    userData?.brandName ||
    userData?.username ||
    (Array.isArray(userData?.contentPillars) && userData.contentPillars.length > 0) ||
    userData?.industry
  );

  const needsOnboarding = userData.onboardingCompleted === false && !userData.fallback && !userData.temporary && !hasBrandBasics;

  // FIX: this used to gate on sessionStorage.getItem('onboarding_checked'),
  // set the instant someone was FIRST redirected here — not after they
  // actually completed the wizard. Backing out with the browser's Back
  // button before finishing permanently spent that flag for the rest of
  // the session, silently landing the user in the dashboard with zero
  // brand data. Always checking the real current state instead — safe
  // from a redirect loop since /brand-setup's own route already passes
  // requireOnboarding={false}, so this check never re-fires there.
  if (requireOnboarding && needsOnboarding) {
    return <Navigate to="/brand-setup" replace />;
  }

  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <ThemeProvider>
            <DraftProvider>
              <SelectedMediaProvider>
                <BrowserRouter>
                  <AppWrapper />
                </BrowserRouter>
              </SelectedMediaProvider>
            </DraftProvider>
          </ThemeProvider>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}